import {
    Checkbox,
    FormControl,
    FormHelperText,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { RequirementField, RequirementFieldOption } from '../data-collection.dto';

interface Props {
    field: RequirementField;
    value: string | string[] | null;
    options: RequirementFieldOption[];
    required: boolean;
    disabled?: boolean;
    disabledReason?: string | null;
    errorMessage?: string | null;
    /** Option values to render disabled (greyed, not selectable) — e.g. buildings already assigned to another meter. */
    disabledValues?: Set<string>;
    onChange: (next: string | string[] | null) => void;
}

/**
 * Controlled SELECT renderer. Persistence is owned by the parent form.
 * Always renders as an MUI Select (single or multiple) — no radio fallback.
 */
export function SelectFieldInput({
    field,
    value,
    options,
    required,
    disabled,
    disabledReason,
    errorMessage,
    disabledValues,
    onChange,
}: Props) {
    const { t } = useTranslation();

    const isMissing =
        required &&
        (value === null || value === undefined || (Array.isArray(value) ? value.length === 0 : value === ''));

    const helperText =
        errorMessage ??
        (disabled ? (disabledReason ?? undefined) : undefined) ??
        field.description ??
        (isMissing ? t('Required') : undefined);

    const isError = Boolean(errorMessage) || isMissing;

    const labelId = `select-${field.id}-label`;
    const labelText = field.label + (required ? ' *' : '');
    const emptyHint = (
        <MenuItem disabled value="">
            <Typography variant="body2" color="text.disabled">
                {t('No options available')}
            </Typography>
        </MenuItem>
    );

    if (field.multiple) {
        const arr = Array.isArray(value) ? value : [];
        return (
            <FormControl size="small" fullWidth error={isError} disabled={disabled}>
                <InputLabel id={labelId}>
                    {field.label}
                    {required && ' *'}
                </InputLabel>
                <Select<string[]>
                    labelId={labelId}
                    multiple
                    value={arr}
                    label={labelText}
                    onChange={(e) => {
                        const raw = e.target.value;
                        const next: string[] = Array.isArray(raw) ? raw : String(raw).split(',');
                        onChange(next);
                    }}
                    renderValue={(picked) =>
                        picked.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ')
                    }
                >
                    {options.length === 0 && emptyHint}
                    {options.map((o) => (
                        <MenuItem
                            key={o.value}
                            value={o.value}
                            // Never disable an option already chosen here, so the user can always un-pick it.
                            disabled={disabledValues?.has(o.value) && !arr.includes(o.value)}
                        >
                            <Checkbox checked={arr.includes(o.value)} size="small" sx={{ p: 0.5 }} />
                            <ListItemText primary={o.label} />
                        </MenuItem>
                    ))}
                </Select>
                {helperText && <FormHelperText>{helperText}</FormHelperText>}
            </FormControl>
        );
    }

    const single = typeof value === 'string' ? value : '';
    return (
        <FormControl size="small" fullWidth error={isError} disabled={disabled}>
            <InputLabel id={labelId}>
                {field.label}
                {required && ' *'}
            </InputLabel>
            <Select<string>
                labelId={labelId}
                value={single}
                label={labelText}
                onChange={(e) => onChange(String(e.target.value) || null)}
                renderValue={(picked) => options.find((o) => o.value === picked)?.label ?? String(picked)}
            >
                {options.length === 0 && emptyHint}
                {/* Escape hatch for a wrongly saved answer: picking "(empty)" clears
                    the selection back to unanswered (saved as a blank value). */}
                {options.length > 0 && (
                    <MenuItem value="">
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('(empty)')}
                        </Typography>
                    </MenuItem>
                )}
                {options.map((o) => (
                    <MenuItem
                        key={o.value}
                        value={o.value}
                        disabled={disabledValues?.has(o.value) && o.value !== single}
                    >
                        <ListItemText primary={o.label} />
                    </MenuItem>
                ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
}
